/**
 * SliceCategoryExplainer — progressive-disclosure explainer for slice and
 * coslice categories, the categorical structures behind PERMIT and DELEGATE.
 *
 * A slice category C/A consists of all objects with a morphism to A. If your
 * object has no arrow to A, it is not in the slice — it has no authority to
 * act. The card includes:
 *
 *   - An interactive "authority check" panel
 *   - A CalloutBox for the ICG-Dt Safety Envelope
 *   - Commuting-triangle visualization in the formal tier
 *   - Concrete examples (API authorization, capability-based systems)
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useCallback, useState } from "react";
import { GRAY, THEOREM_COLOR, withAlpha } from "../../design-system/tokens.js";
import { MathInline, tex } from "../../components/math/index.js";
import {
  CalloutBox,
  ExplainerCard,
  SliceCategoryDiagram,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";

const ACCENT = THEOREM_COLOR.PERMIT;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-slice-category",
  kind: "Categorical structure",
  title: "Slice / Coslice category",
  subtitle: "C/A — objects with a designated morphism to A",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "PERMIT (P)", href: "#explainer-permit", accent: ACCENT },
  { label: "DELEGATE (SDP)", href: "#explainer-delegate" },
  { label: "Adjunction", href: "#explainer-adjunction" },
  { label: "Fibration / Opfibration", href: "#explainer-fibration" },
  { label: "Double category", href: "#explainer-double-category" },
];

// inline: local — operations with their authorization status
interface AuthEntry {
  readonly name: string;
  readonly hasMorphism: boolean;
  readonly description: string;
}

const AUTH_EXAMPLES: readonly AuthEntry[] = [
  { name: "ReadProfile", hasMorphism: true, description: "Has token → UserService" },
  { name: "UpdateEmail", hasMorphism: true, description: "Has token → UserService" },
  { name: "DeleteAccount", hasMorphism: false, description: "No morphism to admin authority" },
  { name: "ReadPublicData", hasMorphism: true, description: "Has token → PublicAPI" },
  { name: "EscalatePrivilege", hasMorphism: false, description: "No morphism to root authority" },
] as const;

function AuthorityCheckDemo(): React.JSX.Element | null {
  const [selected, setSelected] = useState<number>(0);
  const select = useCallback(
    (i: number): (() => void) =>
      () => {
        setSelected(i);
      },
    [],
  );

  const entry = AUTH_EXAMPLES[selected];
  if (!entry) return null;
  return (
    <div
      style={{
        marginTop: "0.75rem",
        padding: "0.75rem",
        backgroundColor: withAlpha(ACCENT, "0D"),
        borderRadius: "0.5rem",
        border: `1px solid ${withAlpha(ACCENT, "33")}`,
      }}
      aria-label="Authority check demonstration"
    >
      <div
        style={{
          fontSize: "0.6875rem",
          fontWeight: 700,
          color: ACCENT,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          marginBottom: "0.5rem",
        }}
      >
        Slice membership check — click an operation
      </div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "0.375rem",
          marginBottom: "0.5rem",
        }}
      >
        {AUTH_EXAMPLES.map((e, i) => (
          <button
            key={e.name}
            type="button"
            onClick={select(i)}
            aria-pressed={selected === i}
            style={{
              appearance: "none",
              border: selected === i ? `2px solid ${ACCENT}` : `1px solid ${GRAY[300]}`,
              background: selected === i ? withAlpha(ACCENT, "1A") : "white",
              borderRadius: "0.3rem",
              padding: "0.2rem 0.5rem",
              cursor: "pointer",
              fontSize: "0.75rem",
              color: e.hasMorphism ? GRAY[900] : GRAY[500],
              fontWeight: 600,
              fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
              textDecoration: e.hasMorphism ? "none" : "line-through",
            }}
          >
            {e.name}
          </button>
        ))}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          padding: "0.5rem",
          borderRadius: "0.3rem",
          backgroundColor: entry.hasMorphism ? withAlpha(ACCENT, "1A") : withAlpha(GRAY[500], "1A"),
          border: `1px solid ${entry.hasMorphism ? withAlpha(ACCENT, "44") : withAlpha(GRAY[500], "44")}`,
        }}
      >
        <span
          style={{
            fontSize: "1.25rem",
            lineHeight: 1,
          }}
        >
          {entry.hasMorphism ? "✓" : "✗"}
        </span>
        <div>
          <div
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "0.8125rem",
              fontWeight: 600,
              color: entry.hasMorphism ? ACCENT : GRAY[600],
            }}
          >
            {entry.name}
            {entry.hasMorphism ? " ∈ C/A" : " ∉ C/A"}
          </div>
          <div style={{ fontSize: "0.75rem", color: GRAY[600] }}>
            {entry.description}
            {!entry.hasMorphism && " — structurally absent from the governed category"}
          </div>
        </div>
      </div>
    </div>
  );
}

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      A <strong>slice category</strong> C/A consists of all objects with a morphism pointing to A.
      If your object has no arrow to A, it is not in the slice — it has no authority to act. CSK
      uses this to make &ldquo;operation without permission&rdquo; structurally absent: the
      unauthorized operation is not denied at runtime, it does not exist in the governed category at
      all.
    </p>
  ),
  intuition: (
    <>
      <p>
        Think of <strong>A</strong> as the &ldquo;authority anchor.&rdquo; Every authorized
        operation is an object X together with a morphism <MathInline>{tex`X \to A`}</MathInline> —
        its permission grant. An unauthorized operation is simply not an object of C/A: it lacks the
        required morphism. The operation is not denied at runtime; it does not exist in the governed
        category at all.
      </p>
      <p>
        <strong>Concrete example:</strong> consider an API where every request must carry a valid
        authorization token pointing to a service principal. The slice C/A has all requests that
        carry such a token. A request without the token is not a &ldquo;rejected request&rdquo; — it
        is simply not a request in the API&rsquo;s type universe. There is no error code for it, no
        handler to bypass, no policy to misconfigure.
      </p>
      <p>
        The dual construction is the <strong>coslice</strong> (A/C): objects with a morphism{" "}
        <em>from</em> A. This represents delegated capabilities flowing outward from A. Where the
        slice says &ldquo;you must prove you have permission,&rdquo; the coslice says &ldquo;A has
        explicitly delegated authority to you — here is the receipt.&rdquo;
      </p>
      <AuthorityCheckDemo />
    </>
  ),
  formal: (
    <>
      <p>
        The <strong>slice category</strong> C/A is the comma category{" "}
        <MathInline>{tex`(\mathrm{Id} \downarrow \Delta_{A})`}</MathInline>. Objects are pairs{" "}
        <MathInline>{tex`(X, f : X \to A)`}</MathInline>. A morphism from{" "}
        <MathInline>{tex`(X, f)`}</MathInline> to <MathInline>{tex`(Y, g)`}</MathInline> is a
        morphism <MathInline>{tex`h : X \to Y`}</MathInline> in C such that{" "}
        <MathInline>{tex`g \circ h = f`}</MathInline> — a commuting triangle:
      </p>
      <SliceCategoryDiagram accent={ACCENT} />
      <p style={{ marginTop: "0.75rem" }}>
        The commutativity condition <MathInline>{tex`g \circ h = f`}</MathInline> ensures that
        authority is preserved under composition: if X has permission (witnessed by f) and we
        refactor through h, the result Y retains permission (witnessed by g) — and the two
        permissions are consistent.
      </p>
      <p>
        The <strong>coslice category</strong> A/C is{" "}
        <MathInline>{tex`(\Delta_{A} \downarrow \mathrm{Id})`}</MathInline>. Objects are pairs{" "}
        <MathInline>{tex`(X, f : A \to X)`}</MathInline>. PERMIT lives in the slice; DELEGATE in the
        coslice. The adjunction between the forgetful functors{" "}
        <MathInline>{tex`C/A \to C`}</MathInline> and <MathInline>{tex`A/C \to C`}</MathInline> is
        the structural backbone of the DELEGATE Structural Duality Principle.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        <strong>PERMIT</strong> (P) makes unauthorized operations structurally inexpressible by
        living in C/A: if you lack the morphism to A, you simply are not an object of the governed
        category. There is no runtime check that can be bypassed, no policy that can be
        misconfigured, no error handler to circumvent — the operation is absent from the
        type-theoretic universe. The seam type is <em>mixed</em> (IMMUNE mode) and the governance
        artifact is a <strong>capability grant</strong>.
      </p>
      <p>
        The SDP <strong>DELEGATE</strong> uses the coslice adjunction to certify that delegation is
        safe: the unit and counit of the{" "}
        <MathInline>{tex`\text{slice} \dashv \text{coslice}`}</MathInline> adjunction witness that
        authority delegation and receipt are complementary. Delegation cannot produce more authority
        than the delegator possesses — this is the G1 (Ambient Effect) generator at work.
      </p>
      <CalloutBox eyebrow="Safety Envelope — ICG-Dt" accent={ACCENT}>
        <p style={{ margin: 0 }}>
          Deploying PERMIT with DELEGATE yields the <strong>ICG-Dt Safety Envelope</strong>: the
          adjunction unit certifies that every delegation has a receipt, and the counit certifies
          that every receipt was issued by a valid delegation. The two-sided certificate is strictly
          stronger than either PERMIT or DELEGATE alone — it makes both <em>unauthorized action</em>{" "}
          (G1 violation) and <em>unauthorized delegation</em> (G1 + G4 compound violation)
          unconstructable in a single envelope.
        </p>
      </CalloutBox>
    </>
  ),
  references: [
    {
      label: "Seam theory",
      path: "theory/structural/SEAM-THEORY.md",
      note: "PERMIT seam classification — mixed seam, IMMUNE mode, capability grant.",
    },
    {
      label: "Foundation/DoubleCategory/Seams.agda",
      path: "agda/Foundation/DoubleCategory/Seams.agda",
      note: "Mechanized DCSeam, DCBridge, SeamBridgePair definitions.",
    },
    {
      label: "Theorem-Canon.md",
      path: "docs/reference/Theorem-Canon.md",
      note: "Extended explanation of PERMIT, DELEGATE, and ICG-Dt Safety Envelope.",
    },
    {
      label: "Seam Normal Form",
      path: "theory/structural/SEAM-NORMAL-FORM.md",
      note: "WITNESS → AUTHORIZE → EXECUTE → ACCOUNT phases and non-feedback invariant.",
    },
    {
      label: "Foundation/Seams/SeamNormalForm.agda",
      path: "agda/Foundation/Seams/SeamNormalForm.agda",
      note: "Mechanized SNF phases B1-B4 and the non-feedback invariant.",
    },
  ],
};

export function SliceCategoryExplainer(): React.JSX.Element {
  return (
    <ExplainerCard
      identity={IDENTITY}
      tiers={TIERS}
      diagram={<SliceCategoryDiagram accent={ACCENT} />}
      crossLinks={CROSS_LINKS}
    />
  );
}
